import _ from 'lodash'
import moment from 'moment'

import { getDurationInHours } from '../server/utils/date'
import timeUtils from '../utils/timeUtils'
import {
  getLastOutlookEventSyncDate,
  getLastOutlookMessageSyncDate,
  getOutlookEventDeltaToken,
  getOutlookMessageDeltaToken,
} from '../server/store'

import { logError, log } from '../utils/logger'
import queryClient from '../queryClient'
import { UNHANDLED_ENTRIES_QUERY_KEY } from '../hooks/queries/keys'
import { getLatestGlimpseToken } from '../redux/services/auth'
import config from '../config'

const { MAX_ELAPSED_HOURS_TO_SYNC_ALL } = config

export const buildInitialSyncMessage = (entity, { startISO, endISO }) => {
  const from = moment(startISO).format('MMMM Do')
  const to = moment(endISO).format('MMMM Do')
  return `${entity} from ${from} to ${to}`
}

export const isOutlookDeltaTokensValid = () => {
  const lastEventSyncDate = getLastOutlookEventSyncDate()
  const lastMessageSyncDate = getLastOutlookMessageSyncDate()
  const lastSyncDate =
    lastEventSyncDate < lastMessageSyncDate ? lastEventSyncDate : lastMessageSyncDate
  const doesDeltaTokenExist =
    !_.isEmpty(getOutlookEventDeltaToken()) && !_.isEmpty(getOutlookMessageDeltaToken())
  if (!(lastSyncDate && doesDeltaTokenExist)) return false
  const timeElapsed = getDurationInHours(lastSyncDate, new Date())
  return Number(timeElapsed) < MAX_ELAPSED_HOURS_TO_SYNC_ALL
}

export const calcIsUserLoggedIn = async auth0 => {
  // TODO: if not connected then we get token from the store getGlimpseApiToken
  // else continue
  const haveToken = Boolean(await getLatestGlimpseToken(auth0))
  const _isOutlookDeltaTokensValid = isOutlookDeltaTokensValid()
  return (config.IS_TEST_E2E || _isOutlookDeltaTokensValid) && haveToken
}

export const deleteUnhandledEntriesForDate = dateStr => {
  try {
    const day = moment.utc(dateStr).format('YYYY-MM-DD')
    log('Deleting unhandled entry for date: ', day)
    queryClient.setQueryData(UNHANDLED_ENTRIES_QUERY_KEY, (prev = []) =>
      prev.filter(e => !e.start.startsWith(day)),
    )
  } catch (e) {
    logError(e, 'app/utils > deleteUnhandledEntriesForDate > e: ')
  }
}

export const getUnhandledEntriesForDate = dateStr => {
  try {
    const day = moment.utc(dateStr).format('YYYY-MM-DD')
    const all = queryClient.getQueryData(UNHANDLED_ENTRIES_QUERY_KEY) ?? []
    return all.filter(e => e.start.startsWith(day))
  } catch (e) {
    logError(e, 'app/utils > getUnhandledEntriesForDate > e: ')
  }
}

const matchAnyNumberLengthRegex = /[0-9]+/g
export const replaceErrorMessageDynamicData = errorMessage =>
  errorMessage.replace(matchAnyNumberLengthRegex, '[NUMBER_PLACEHOLDER]')

export const getCsvInfoTotal = csvEntries => {
  const csvEntriesInfo = _.map(csvEntries, e => {
    const { miliseconds } = timeUtils.getTimeUnitsBetweenDates(e.start, e.end)
    return {
      start: moment(e.start).format('MM/DD/YY'),
      date: e.start,
      durationInHours: Number(e.durationInHours),
      durationInMiliseconds: miliseconds,
    }
  })
  const groupedCsvEntriesInfo = _.values(_.groupBy(csvEntriesInfo, 'start'))
  const csvInfoByTimesheetTotalUnordered = _.map(groupedCsvEntriesInfo, e => {
    return {
      start: e[0].start,
      date: new Date(e[0].date),
      durationInHours: _.sumBy(e, 'durationInHours'),
      durationInMiliseconds: _.sumBy(e, 'durationInMiliseconds'),
      count: e.length,
    }
  })

  const csvInfoTotal = {
    count: _.sumBy(csvInfoByTimesheetTotalUnordered, 'count'),
    durationInHours: _.sumBy(csvInfoByTimesheetTotalUnordered, 'durationInHours'),
    durationInMiliseconds: _.sumBy(csvInfoByTimesheetTotalUnordered, 'durationInMiliseconds'),
    timesheetsCount: csvInfoByTimesheetTotalUnordered.length,
    csvInfoByTimesheetTotal: _.orderBy(csvInfoByTimesheetTotalUnordered, ['date'], ['asc']),
  }

  return csvInfoTotal
}

export const getNameInitials = name => {
  const getInitial = str => _.upperCase(str.charAt(0))

  const splitedName = _.split(name, ' ', 2)
  return _.join(_.map(splitedName, getInitial), '')
}

export const getProject = ({ projects, projectRepliconId, projectId }) => {
  return projects.find(project => {
    return project.repliconId === projectRepliconId || project._id === projectId
  })
}

const BETA_HOSTNAME = 'beta.glimpse.keystonestrategy.io'

const BETA_USERS = [
  '58daa8fd82f38b00119a2b93',
  '5b69ecc11c889c002803609c',
  '5c6dc01e61495e002812aaa0',
  '5d000888faa86200292cae4b',
  '5e0e402643f0a7002b788eb2',
  '5ed1a477995cb30021d14a54',
  '603941a28f4aee0017fb5adb',
  '604bbccbdc82610017441897',
  '606e1acf283dcc0017da4c9b',
  '609415254bbe4b001713d394',
  '60a7f7e3b0e870001738b039',
  '618a6131704e2300185684b1',
  '620bc5b82be7e00018de8705',
  '62164cf5bf49c20018cd6166',
  '622b5d860bf1e500187a5ec5',
  '6238e3d491d1840018d0f145',
  '623e39bfa1ed5200186dae55',
  '624dc991b990bd00181f4009',
  '62827445590e440018b2be67',
  '628bd0c2cbf5130018ea7779',
  '62d97b6ee3c4c00018677559',
  '62dac30ff650380018900ca3',
  '62e92c527c15e50018d2f571',
  '6368d10a847c86001810c533',
  '6396e2e4b7378a0018e1aa94',
  '64c4182b248d5c0014d1cd2e',
  '6511b44aa301060014a7b8b5',
  '653be88c4df45b0014c6647a',
  '655696101fdf2700149a2b66',
  '65582b7fce806400144e318d',
  '655e736a045601001479453d',
  '65774f5f02aca30014affa25',
  '65d625e87055590015c721b6',
  '662a0945217ff981dccf49ab',
  '664519a1e4d322d9fd5bf63f',
  '664e0dc766e1147e2b6f9e7b',
  '6663c522ef3301ff787761a7',
  '66675e553393a20374783014',
  '6679d140248df0805052556c',
  '668b987ce4c6f90cc02bc6df',
  '66e1f7b79512e439cbaa5997',
  '67167d98330f2c29507d1e8b',
  '672e1c7fcad5713cca3fec1b',
  '674485f205da11f2f87a0299',
  '6761f2a97dd28f8b0154f019',
  '67a3ee52600863283c74d3b7',
  '67afaacc8714eb96d4bc5347',
  '67c0b72e6621435b11062e2a',
  '681118edcc53484190509ac5',
  '682f51a30b0fef5a7ff6b67b',
  '68375df7122c026f5f27f54b',
  '670418eea3d7c37a00e39065',
  '58daa8fd82f38b00119a2bd4',
  '68375df7122c026f5f27f54a',
  '674f11f5633ebfa4acddf516',
  '66a799dd93adaecdc3fcb738',
  '65119f1aa301060014a75fbd',
  '58daa8fd82f38b00119a2ad6',
  '58daa8fd82f38b00119a2a66',
  '63a0a0e80ad7c7001887a4e3',
  '653fccba4852cc00141372a3',
  '675c8273b1ff84f3785e35fc',
  '67534ab2ac20e8c6118d5de1',
  '67eaaf0859dd54b53811bf79',
]

export const redirectIfNotBetaUser = userId => {
  if (window.location.hostname !== BETA_HOSTNAME) return

  if (!BETA_USERS.includes(userId)) {
    window.location.href = 'https://glimpse.keystonestrategy.io/'
  }
}
